import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Is there a history entry BEFORE the current one that we can safely return to?
 *
 * React Router keeps its own cursor into the session history at `window.history.state.idx`
 * — `0` for the first entry of the document, incremented on every push, and left ALONE by
 * a `replace`. That last property is why we prefer it over `location.key !== 'default'`
 * (the check this hook used to make on its own): `'default'` is the key of the first
 * entry, but a `replace` mints a fresh key without adding an entry. So a page that
 * redirects itself — a create form swapping itself for the record it just created, or an
 * auth guard bouncing a deep link — used to read as "has history", and `navigate(-1)`
 * walked the user clean out of the SPA.
 *
 * `location.key` stays as the fallback for routers that do not populate `idx`
 * (MemoryRouter / HashRouter, and therefore most component tests).
 */
export const hasPreviousHistoryEntry = ({ historyIndex, locationKey }) => {
  if (typeof historyIndex === 'number') {
    return historyIndex > 0;
  }

  return locationKey !== 'default';
};

/**
 * Back-button handler for any page that has a parent.
 *
 * `goBack(parentUrl)` returns to wherever the user actually came from, and only falls back
 * to `parentUrl` when there is nowhere to go back to — a deep link, a new tab, a bookmark,
 * or a page that replaced the first history entry. Pass the page's PARENT (the pipeline a
 * run belongs to, the list a record belongs to), not "the list" reflexively: the fallback
 * is what the user sees on exactly those entry paths.
 *
 * Do NOT use this for post-delete navigation — the previous entry is often the record that
 * no longer exists. Navigate to the list explicitly there.
 */
const useGoBack = () => {
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (defaultUrl) => {
      const canGoBack = hasPreviousHistoryEntry({
        historyIndex: window.history?.state?.idx,
        locationKey: location.key,
      });

      if (canGoBack) {
        navigate(-1);
        return;
      }

      if (defaultUrl) {
        navigate(defaultUrl);
      }
    },
    [location.key, navigate],
  );
};

export default useGoBack;
